/** Methodology page — required compliance disclosure. Linked from every
 *  MethodologyBanner. Plain prose explaining DIRECT vs PANEL vs MODEL vs HYBRID. */
import { useNavigate, Link } from "react-router-dom";
import { T } from "@/lib/theme";

const C = T.dark;

export default function Methodology() {
  const nav = useNavigate();

  const layer = (badge, badgeColor, title, body) => (
    <section style={{ marginBottom: 32 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
        <span style={{ background: badgeColor, color: "#0A1628", padding: "3px 10px", borderRadius: 4,
          fontSize: 11, fontWeight: 800, letterSpacing: "0.08em" }}>{badge}</span>
        <h2 style={{ fontSize: 19, fontWeight: 700, color: C.text, margin: 0 }}>{title}</h2>
      </div>
      <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.65, margin: 0, maxWidth: 720 }}>{body}</p>
    </section>
  );

  return (
    <div data-testid="methodology-page" style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'Inter',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@700;800&display=swap'); body{margin:0;background:${C.bg}}`}</style>

      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "14px 28px",
        display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Link to="/" style={{ textDecoration: "none", color: C.text, fontWeight: 800, fontSize: 18 }}>
          WaveScore<span style={{ color: T.greenAccent }}>Pro</span>
        </Link>
        <button data-testid="methodology-back" onClick={() => nav(-1)}
          style={{ background: "transparent", color: C.text, border: `1px solid ${C.border}`,
            padding: "6px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
          ← Back
        </button>
      </div>

      <div style={{ maxWidth: 780, margin: "0 auto", padding: "48px 28px 80px" }}>
        <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 10 }}>
          Methodology · how we measure
        </div>
        <h1 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 38, fontWeight: 800, margin: "0 0 14px", letterSpacing: "-0.02em" }}>
          The WaveScore Method
        </h1>
        <p style={{ color: C.muted, fontSize: 15, lineHeight: 1.7, margin: "0 0 36px", maxWidth: 680 }}>
          Audience measurement in West Africa has historically meant quarterly diary surveys — accurate when fresh, stale by the time the
          numbers ship. WaveScorePro blends four data layers so every chart you see is labelled with the confidence behind it. We will
          never display a number without telling you where it came from.
        </p>

        {layer("DIRECT", "#34D399", "Direct stream measurement",
          "We continuously poll the public Icecast / Shoutcast endpoints of every streaming station in our catalogue. Each poll returns the current number of TCP connections to the stream — a deterministic count of online listeners. This is the most reliable layer of the stack. Where DIRECT is available, that number is what you see. Device counts are converted to people using a persons-per-connection multiplier anchored to GSS 2021 PHC (household size × radio-listenership) and GLSS-7.")}

        {layer("PANEL", "#FBBF24", "USSD panel survey (Hubtel)",
          "For non-streaming (FM-only) stations and for offline audience modelling, WaveScorePro uses a USSD-based listener panel delivered via Hubtel. Panel Phase 1 launches Q3 2026, targeting 500 initial respondents across Greater Accra and Ashanti, scaling to 5,000 respondents across all 16 regions by Q4 2026. Until the panel is live, non-streaming station estimates are labelled MODEL and carry 60–75% confidence. We will never label data PANEL until real panel responses are included in the calculation.")}

        {layer("MODEL", "#60A5FA", "Statistical model",
          "When neither DIRECT nor PANEL data is available for the slot you're querying (e.g. demographic breakdown for a station that doesn't stream), we apply a probabilistic model trained on the historical DIRECT + PANEL pairs we've collected. The model output is clearly labelled MODEL on every chart, and we publish the model's accuracy versus the panel as a public benchmark each quarter.")}

        {layer("HYBRID", "#C084FC", "Hybrid (panel + model)",
          "Our default ratings layer blends PANEL responses against MODEL output, weighting in favour of PANEL where the sample is large enough. This is the closest analogue to traditional RAM (Radio Audience Measurement) figures published by Ipsos / Geopoll / Kantar and is the figure most often used in trading decisions.")}

        <section data-testid="methodology-country-calibration" style={{ marginTop: 40, padding: 22,
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
          <h3 style={{ color: C.text, fontSize: 17, fontWeight: 700, margin: "0 0 10px" }}>
            Country calibration
          </h3>
          <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.65, margin: "0 0 12px" }}>
            Only one West African market — <strong style={{ color: T.greenAccent }}>Ghana</strong> — is fully
            calibrated today. The others are honestly labelled so you never see a number without knowing what backs it:
          </p>
          <ul style={{ margin: 0, paddingLeft: 20, color: C.muted, fontSize: 13, lineHeight: 1.85 }}>
            <li><strong style={{ color: "#34D399" }}>Ghana · CALIBRATED</strong> — anchored to <strong>GSS 2021 PHC</strong> + GLSS-7. LSM bands (SAARF ladder) available. Panel-corrected where a USSD panel is running.</li>
            <li><strong style={{ color: "#FBBF24" }}>Nigeria · MODEL</strong> — live streaming counts captured but not calibrated against NBS census figures. Every audience number carries a MODEL badge. Excluded from ranking positions and certificates.</li>
            <li><strong style={{ color: "#94A3B8" }}>Senegal · COMING SOON</strong> — not yet measured. Browsable stations only; no audience numbers surfaced.</li>
            <li><strong style={{ color: "#94A3B8" }}>Côte d'Ivoire · COMING SOON</strong> — same as Senegal.</li>
          </ul>
        </section>

        <section data-testid="methodology-certificate-flow" style={{ marginTop: 40, padding: 22,
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
          <h3 style={{ color: C.text, fontSize: 17, fontWeight: 700, margin: "0 0 10px" }}>
            The certificate flow
          </h3>
          <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.65, margin: "0 0 10px" }}>
            The detection ladder is <strong>DETECTED → PENDING → CONFIRMED → CERTIFIED</strong>. A metadata match on
            the station's now-playing string is a <em>sighting</em>, not proof. A row only reaches CERTIFIED when an
            independent audio fingerprint match confirms the actual audio aired. Until fingerprint verification is
            live (Wave 3), the ladder tops out at <strong>CONFIRMED</strong> and no certificate is minted from
            metadata alone.
          </p>
          <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.65, margin: 0 }}>
            Admins can issue certificates manually — those are stamped
            <em> "Manually issued · not fingerprint-verified" </em> on the PDF face and can never claim independent
            proof. Certificates carry a diagonal <strong>NCA licence watermark</strong> when the station was claimed
            under our hardened NCA-registered-email flow. Verify any certificate at{" "}
            <code style={{ color: T.greenAccent }}>/verify/&lt;certificate-number&gt;</code>.
          </p>
        </section>

        <section data-testid="methodology-crawl-engine" style={{ marginTop: 40, padding: 22,
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
          <h3 style={{ color: C.text, fontSize: 17, fontWeight: 700, margin: "0 0 10px" }}>
            The Credible Crawl Engine
          </h3>
          <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.65, margin: "0 0 10px" }}>
            DIRECT numbers are produced by an open-web crawler that probes public
            Icecast / Shoutcast stats endpoints in a specific order (the "probe ladder"):
          </p>
          <ol style={{ margin: "0 0 12px", paddingLeft: 20, color: C.muted, fontSize: 13, lineHeight: 1.9 }}>
            <li>Icecast <code style={{ color: T.greenAccent }}>/status-json.xsl</code></li>
            <li>Shoutcast v1 <code style={{ color: T.greenAccent }}>/7.html</code></li>
            <li>Shoutcast v2 <code style={{ color: T.greenAccent }}>/stats?sid=1&amp;json=1</code></li>
            <li>ICY in-band metadata (last resort — title only, no count)</li>
          </ol>
          <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.65, margin: "0 0 10px" }}>
            The crawler is polite: one hit per host every 30 seconds, exponential
            backoff on 429/5xx, and it honours <code style={{ color: T.greenAccent }}>robots.txt</code>. Its
            identifying User-Agent is{" "}
            <code style={{ color: T.greenAccent }}>WaveScorePro-Crawler/1.0</code> —
            see <Link to="/crawler" style={{ color: T.greenAccent }}>/crawler</Link> for the full policy and a
            live coverage snapshot.
          </p>
          <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.65, margin: 0 }}>
            <strong>Anti-gaming sanity layer</strong> — every crawled number passes
            three defences before publication: (1) endpoint authenticity
            (only structurally-valid Icecast/Shoutcast responses count),
            (2) endpoint dedupe (two stations sharing one mount-point can't
            double-count), and (3) spike freeze (anything more than 3× the
            trailing 24h mean is frozen to the 90-day P95 and flagged for
            review — legitimate spikes thaw after two consistent cycles).
          </p>
        </section>

        <section style={{ marginTop: 48, padding: 20, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
          <h3 style={{ color: C.text, fontSize: 16, fontWeight: 700, margin: "0 0 8px" }}>Compliance & transparency</h3>
          <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, margin: 0 }}>
            We comply with Ghana's Data Protection Act, 2012 (Act 843). USSD panel respondents give explicit opt-in consent before any
            personal data is processed. Inferred demographic data (MODEL output) is disclosed as such on every chart. Methodology audits
            are commissioned annually from an independent statistician. The previous audit letter is available on request from{" "}
            <a href="mailto:methodology@wavescorepro.com" style={{ color: T.greenAccent }}>methodology@wavescorepro.com</a>.
          </p>
        </section>
      </div>

      <footer style={{ borderTop: `1px solid ${C.border}`, padding: "24px 28px", textAlign: "center", color: C.muted, fontSize: 12 }}>
        © {new Date().getFullYear()} I-Assist Pro Limited · Accra, Ghana
      </footer>
    </div>
  );
}
